import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 25, 2025";

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-[#1e3a5f]">Terms of Service</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-6">
          <div className="text-sm text-gray-500">Last updated: {lastUpdated}</div>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using Honey™ Cooking Companion ("the Service"), you agree to be bound 
              by these Terms of Service. If you do not agree to these terms, please do not use 
              the Service.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Description of Service</h2>
            <p className="text-gray-700 leading-relaxed">
              Honey Cooking Companion is a recipe management and cooking assistance application that 
              provides features including:
            </p>
            <ul className="list-disc list-inside ml-4 text-gray-700 space-y-1">
              <li>Recipe creation, import, and organization</li>
              <li>AI-powered recipe generation and cooking advice</li>
              <li>Meal planning and shopping list generation</li>
              <li>Social features including Hives (community groups) and Buzz (posts)</li>
              <li>Mise en Place (coordinated cooking timelines)</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">User Accounts</h2>
            <div className="text-gray-700 leading-relaxed space-y-2">
              <p>To use certain features, you must create an account using Google Sign-In. You agree to:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Provide accurate information</li>
                <li>Maintain the security of your account</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be responsible for all activity under your account</li>
              </ul>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">User Content</h2>
            <div className="text-gray-700 leading-relaxed space-y-2">
              <p>
                You retain ownership of content you create ("User Content"), including recipes, 
                photos, notes, and posts. By submitting User Content, you grant us a non-exclusive, 
                worldwide, royalty-free license to use, display, and distribute your content in 
                connection with the Service.
              </p>
              <p>You agree not to post content that:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Infringes on others' intellectual property rights</li>
                <li>Is harmful, abusive, or harassing</li>
                <li>Contains malware or harmful code</li>
                <li>Is spam or misleading</li>
                <li>Violates any applicable laws</li>
              </ul>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Acceptable Use</h2>
            <p className="text-gray-700 leading-relaxed">You agree not to:</p>
            <ul className="list-disc list-inside ml-4 text-gray-700 space-y-1">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Scrape, harvest, or collect user data without permission</li>
              <li>Impersonate others or misrepresent your affiliation</li>
              <li>Use automated systems or bots without our permission</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">AI-Generated Content</h2>
            <div className="text-gray-700 leading-relaxed space-y-2">
              <p>
                Our Service uses artificial intelligence to generate recipes, provide cooking advice, 
                and offer suggestions. You acknowledge that:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>AI-generated content is provided for informational purposes only</li>
                <li>AI suggestions may not be accurate or suitable for your specific situation</li>
                <li>You are responsible for verifying cooking times, temperatures, and food safety</li>
                <li>We are not liable for any issues arising from following AI-generated advice</li>
              </ul>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Food Safety Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed">
              <strong>Important:</strong> The Service provides recipes and cooking guidance but does 
              not replace professional culinary or nutritional advice. Always follow proper food 
              safety guidelines, check for allergens, and consult healthcare professionals for 
              dietary restrictions or medical conditions. We are not responsible for any adverse 
              reactions, food safety issues, or health problems that may result from using recipes 
              or advice from the Service.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Intellectual Property</h2>
            <div className="text-gray-700 leading-relaxed space-y-2">
              <p>
                The Service, including its design, features, and content (excluding User Content), 
                is owned by Honey Cooking Companion and protected by intellectual property laws. 
                The Honey name, logo, and related trademarks are our property.
              </p>
              <p>
                You may not copy, modify, distribute, or create derivative works of our Service 
                without permission.
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed">
              Our Service may integrate with third-party services (e.g., Google Sign-In, AI providers). 
              Your use of these services is subject to their respective terms and privacy policies. 
              We are not responsible for third-party services.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Disclaimer of Warranties</h2>
            <p className="text-gray-700 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, 
              ERROR-FREE, OR SECURE.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF 
              THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US (IF ANY) 
              IN THE PAST TWELVE MONTHS.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              We may suspend or terminate your access to the Service at any time for violation of 
              these terms or any other reason. You may delete your account at any time. Upon 
              termination, your right to use the Service ceases immediately.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may modify these terms at any time. We will notify you of material changes by 
              posting updated terms on this page. Your continued use of the Service after changes 
              constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These terms are governed by the laws of the United States and the state of your 
              residence, without regard to conflict of law principles.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> <a href="mailto:honeycookingcompanion@gmail.com" className="text-amber-600 hover:underline">honeycookingcompanion@gmail.com</a>
            </p>
          </section>

          <div className="pt-6 border-t text-center text-sm text-gray-500">
            Honey™ Cooking Companion
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
